// frontend/src/components/Calendar/ScheduleMeetingModal.jsx
// Modal para agendar reunião no Google Calendar com link do Meet.
// Props: { isOpen, onClose, contact, onScheduleSuccess? }
// contact deve ter: id, first_name, email (opcional), company_name (opcional)
// onScheduleSuccess: callback opcional chamado após agendamento bem-sucedido
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';

// Returns a datetime-local string (YYYY-MM-DDTHH:MM) for tomorrow at 15:00 local time
const getDefaultDatetime = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(15, 0, 0, 0);
  // Format as YYYY-MM-DDTHH:MM (datetime-local value)
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(tomorrow.getDate())}` +
    `T${pad(tomorrow.getHours())}:${pad(tomorrow.getMinutes())}`
  );
};

const buildDefaultTitle = (contact) => {
  if (!contact) return '';
  const company = contact.company_name || contact.company?.name || '';
  return `Reunião — ${contact.first_name}${company ? ` (${company})` : ''}`;
};

const ScheduleMeetingModal = ({ isOpen, onClose, contact, onScheduleSuccess }) => {
  const [title, setTitle] = useState('');
  const [datetime, setDatetime] = useState('');
  const [duration, setDuration] = useState('30');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset state and seed defaults each time the modal opens
  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setDatetime('');
      setDuration('30');
      setNotes('');
      setSubmitting(false);
      return;
    }
    setTitle(buildDefaultTitle(contact));
    setDatetime(getDefaultDatetime());
    setDuration('30');
    setNotes('');
    setSubmitting(false);
  }, [isOpen, contact]);

  if (!isOpen) return null;

  const isSubmitDisabled = submitting || !title.trim() || !datetime;

  const handleSchedule = async () => {
    if (isSubmitDisabled) return;
    setSubmitting(true);
    try {
      const startIso = new Date(datetime).toISOString();
      // Timeout propio: el actuador n8n (Calendar + Meet) puede superar el
      // default de 10s del cliente axios.
      const res = await api.post('/calendar/actuator/schedule', {
        contactId: contact.id,
        title: title.trim(),
        start_iso: startIso,
        duration_minutes: parseInt(duration, 10),
        notes: notes.trim() || undefined,
      }, { timeout: 30000 });

      if (res.data.success) {
        const { event_link, no_attendee } = res.data.data || {};
        if (no_attendee) {
          toast.success('Reunião agendada! (sem convite — lead sem email). Veja no Google Calendar.');
        } else {
          toast.success('Reunião agendada! Veja no Google Calendar.');
        }
        onScheduleSuccess?.();
        onClose();
      } else {
        toast.error('Erro ao agendar reunião. Tente novamente.');
      }
    } catch (e) {
      const msg = e.response?.data?.message;
      toast.error(msg || 'Erro ao agendar reunião. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const hasEmail = !!contact?.email;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Agendar Reunião
          </h3>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Email info / amber notice */}
          {hasEmail ? (
            <div className="flex items-start gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md">
              <span className="text-blue-700 dark:text-blue-300 text-xs leading-relaxed">
                Convite será enviado para <strong>{contact.email}</strong> (do calendário de Pedro, com link do Meet).
              </span>
            </div>
          ) : (
            <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-600 rounded-md">
              <span className="text-amber-700 dark:text-amber-300 text-xs leading-relaxed">
                Lead sem email — o evento será criado sem convite.
              </span>
            </div>
          )}

          {/* Título */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Título
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              placeholder="Título da reunião…"
            />
          </div>

          {/* Data e hora */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Data e hora
            </label>
            <input
              type="datetime-local"
              value={datetime}
              onChange={(e) => setDatetime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          {/* Duração */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Duração
            </label>
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="15">15 minutos</option>
              <option value="30">30 minutos</option>
              <option value="45">45 minutos</option>
              <option value="60">60 minutos</option>
            </select>
          </div>

          {/* Notas */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
              Notas (opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 resize-y"
              placeholder="Observações para a reunião…"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSchedule}
            disabled={isSubmitDisabled}
            className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Agendando…' : 'Agendar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleMeetingModal;
